/**
 * Role Icons — React SVG components
 *
 * Inline SVGs that support currentColor, allowing icons to inherit the
 * badge text color for proper color harmony.
 *
 * Matches the ComponentType<SVGProps<SVGSVGElement>> signature used by
 * analysis-icons.tsx and Lucide icons.
 */

import type { SVGProps } from "react";

/**
 * Member / User icon — person silhouette with secondary smaller person
 */
export function MemberRoleIcon(props: Readonly<SVGProps<SVGSVGElement>>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M15,19.13c.85.25,1.74.37,2.62.37,1.43,0,2.84-.32,4.12-.95.1-2.28-1.67-4.2-3.95-4.3-1.43-.06-2.78.62-3.59,1.8M15,19.13h0c0-1.12-.28-2.16-.79-3.07M15,19.13v.11c-1.92,1.16-4.13,1.77-6.38,1.77-2.33,0-4.51-.65-6.37-1.77v-.11c0-3.52,2.85-6.38,6.37-6.38,2.33,0,4.47,1.27,5.59,3.31M12,6.38c0,1.86-1.51,3.38-3.38,3.38s-3.38-1.51-3.38-3.38h0c0-1.86,1.51-3.38,3.38-3.38s3.38,1.51,3.38,3.38ZM20.25,8.62c0,1.45-1.18,2.62-2.62,2.62s-2.62-1.18-2.62-2.62,1.18-2.62,2.62-2.62,2.62,1.18,2.62,2.62Z" />
    </svg>
  );
}

/**
 * Admin icon — person silhouette with shield
 */
export function AdminRoleIcon(props: Readonly<SVGProps<SVGSVGElement>>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M15,19.13,13.72,19.9c-1.6.72-3.34,1.1-5.1,1.1-2.33,0-4.51-.65-6.37-1.77v-.11c0-3.52,2.85-6.38,6.37-6.38,1.13,0,2.22.3,3.17.84M12,6.38c0,1.86-1.51,3.38-3.38,3.38s-3.38-1.51-3.38-3.38h0c0-1.86,1.51-3.38,3.38-3.38s3.38,1.51,3.38,3.38Z" />
      <path d="M19.22,19.53l-3.01,2.18-3.01-2.18c-.89-.64-1.41-1.67-1.41-2.77v-5.65h8.84v5.65c0,1.1-.52,2.12-1.41,2.77Z" />
    </svg>
  );
}

/**
 * System User icon — person silhouette with shield containing a gear
 */
export function SystemUserRoleIcon(props: Readonly<SVGProps<SVGSVGElement>>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M15,19.13,13.72,19.9c-1.6.72-3.34,1.1-5.1,1.1-2.33,0-4.51-.65-6.37-1.77v-.11c0-3.52,2.85-6.38,6.37-6.38,1.13,0,2.22.3,3.17.84M12,6.38c0,1.86-1.51,3.38-3.38,3.38s-3.38-1.51-3.38-3.38h0c0-1.86,1.51-3.38,3.38-3.38s3.38,1.51,3.38,3.38Z" />
      <path d="M19.22,19.53l-3.01,2.18-3.01-2.18c-.89-.64-1.41-1.67-1.41-2.77v-5.65h8.84v5.65c0,1.1-.52,2.12-1.41,2.77Z" />
      <circle cx="16.21" cy="15" r="1.5" />
      <line x1="16.21" y1="13" x2="16.21" y2="13.5" />
      <line x1="16.21" y1="16.5" x2="16.21" y2="17" />
      <line x1="14.48" y1="14" x2="14.91" y2="14.25" />
      <line x1="17.51" y1="15.75" x2="17.94" y2="16" />
      <line x1="14.48" y1="16" x2="14.91" y2="15.75" />
      <line x1="17.51" y1="14.25" x2="17.94" y2="14" />
    </svg>
  );
}

/**
 * Superuser icon — person silhouette with shield containing a plus symbol
 */
export function SuperuserRoleIcon(props: Readonly<SVGProps<SVGSVGElement>>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M15,19.13,13.72,19.9c-1.6.72-3.34,1.1-5.1,1.1-2.33,0-4.51-.65-6.37-1.77v-.11c0-3.52,2.85-6.38,6.37-6.38,1.13,0,2.22.3,3.17.84M12,6.38c0,1.86-1.51,3.38-3.38,3.38s-3.38-1.51-3.38-3.38h0c0-1.86,1.51-3.38,3.38-3.38s3.38,1.51,3.38,3.38Z" />
      <path d="M19.22,19.53l-3.01,2.18-3.01-2.18c-.89-.64-1.41-1.67-1.41-2.77v-5.65h8.84v5.65c0,1.1-.52,2.12-1.41,2.77Z" />
      <line x1="14.09" y1="15.54" x2="18.47" y2="15.54" />
      <line x1="16.28" y1="13.35" x2="16.28" y2="17.72" />
    </svg>
  );
}
